"use client";
import { useTranslations } from "next-intl";

import { useState, useMemo, useEffect } from "react";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import CustomButton from "../ui/custom-button";
import { useIsMobileLandscape } from "@/hooks/use-mobile-landscape";
import { useIsMobile } from "@/hooks/use-mobile";
import useMediaQuery from "@/hooks/use-media-query";

import { CUSTOMER_REVIEWS, type CustomerReview } from "@/lib/reviews";
import { SECTION_CONTAINER, SECTION_HEADING, SECTION_PADDING } from "@/lib/home-section-layout";

interface TestimonialsSectionProps {
  className?: string;
  testimonials?: readonly CustomerReview[];
}

/**
 * The carousel arrows appear at two places in the layout — inside the left
 * column on desktop, and below the cards on mobile — and CSS, not JavaScript,
 * decides which one is visible, so both are always in the tree. One component
 * for both so a change to the buttons (an aria-label, a handler, a hit area)
 * cannot land on one copy and miss the other.
 *
 * The caller supplies the display utility (`hidden md:flex` / `md:hidden flex`)
 * along with its layout classes, rather than this component defaulting to
 * `flex` and relying on tailwind-merge to resolve the conflict.
 */
function CarouselNav({
  className,
  onPrev,
  onNext,
}: {
  className?: string;
  onPrev: () => void;
  onNext: () => void;
}) {
  const buttonClassName =
    "rounded-full w-12 h-12 flex items-center justify-center text-black transition-colors bg-yellow-500 hover:bg-yellow-600";

  return (
    <div className={cn("space-x-4", className)}>
      <button
        aria-label="View previous testimonial"
        onClick={onPrev}
        className={buttonClassName}
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      <button
        aria-label="View next testimonial"
        onClick={onNext}
        className={buttonClassName}
      >
        <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  );
}

export function TestimonialsSection({
  className,
  testimonials = CUSTOMER_REVIEWS,
}: TestimonialsSectionProps) {
  const t = useTranslations('testimonials');
  const [mounted, setMounted] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const isMobile = useMediaQuery("(max-width: 900px)");
  const isMobileHook = useIsMobile();
  const isTablet = useMediaQuery("(max-width: 1024px)");
  const isMobileLandscape = useIsMobileLandscape();

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Use safe values until the component is mounted
  const safeIsMobile = mounted ? isMobile : false;
  const safeIsTablet = mounted ? isTablet : false;
  const safeIsMobileLandscape = mounted ? isMobileLandscape : false;
  const safeIsMobileHook = mounted ? isMobileHook : false;

  const itemsPerPage = safeIsMobile ? 1 : safeIsTablet ? 2 : 3;
  // Nothing to page to when every review is already on screen. Render no
  // control at all rather than one that can never be operated.
  const canPage = testimonials.length > itemsPerPage;

  const handlePrev = () => {
    setActiveIndex(
      (prev) =>
        (prev - itemsPerPage + testimonials.length) % testimonials.length
    );
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + itemsPerPage) % testimonials.length);
  };

  // A page never holds more cards than there are reviews. Asking for
  // itemsPerPage regardless is unsound at both ends: on an empty array the
  // `% length` below is NaN, so every slot is undefined and reading
  // `testimonial.id` throws, and on an array shorter than a page the indices
  // wrap onto themselves and repeat a React key.
  const visibleCount = Math.min(itemsPerPage, testimonials.length);

  // Calculate visible testimonials (current and next)
  const visibleTestimonials = useMemo(() => {
    return Array.from({ length: visibleCount }, (_, i) => {
      return testimonials[(activeIndex + i) % testimonials.length];
    });
  }, [activeIndex, visibleCount, testimonials]);

  // Show a placeholder during hydration
  if (!mounted) {
    return (
      <section data-surface="raised" className={cn(SECTION_PADDING, "bg-site-card font-outfit overflow-hidden", className)}>
        <div className={cn(SECTION_CONTAINER, "animate-pulse")}>
          <div className="h-8 bg-gray-700 rounded w-64 mb-4"></div>
          <div className="h-4 bg-gray-700 rounded w-48 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1,2,3].map(i => (
              <div key={i} className="h-64 bg-gray-700 rounded-3xl"></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="testimonials"
      role="region"
      aria-label="Customer testimonials and reviews"
      data-surface="raised"
      className={cn(
        SECTION_PADDING,
        "bg-site-card font-outfit overflow-hidden",
        className
      )}
    >
      <div className={cn(SECTION_CONTAINER, safeIsMobileLandscape && "!mx-6")}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Left side - Title and navigation */}
          <div className="md:col-span-5 xl:col-span-3   flex flex-col">
            <h2 className={SECTION_HEADING}>
              {t('sectionTitle')}
            </h2>
            <p className="text-white mb-8">
              {t('sectionSubtitle')}
            </p>

            <div>
              <CustomButton
                variant="capsule"
                size={safeIsMobileHook ? "md" : "lg"}
                className="mt-4"
                onClick={() => {
                  window.open("https://www.google.com/maps/place/Miller+40+Car+Care+%26+Tire+Center/@25.7336313,-80.3070954,17z/data=!4m18!1m9!3m8!1s0x88d9b86c2037b817:0xcb2b3ee32c96b6d8!2sMiller+40+Car+Care+%26+Tire+Center!8m2!3d25.7336313!4d-80.3045205!9m1!1b1!16s%2Fg%2F11c52h4rf_!3m7!1s0x88d9b86c2037b817:0xcb2b3ee32c96b6d8!8m2!3d25.7336313!4d-80.3045205!9m1!1b1!16s%2Fg%2F11c52h4rf_?entry=ttu&g_ep=EgoyMDI1MDYzMC4wIKXMDSoASAFQAw%3D%3D", "_blank");
                }}
              >
                {t('viewAllReviews')}
              </CustomButton>
            </div>
            {/* Desktop */}
            {canPage && (
              <CarouselNav
                className="hidden md:flex mt-auto pt-6"
                onPrev={handlePrev}
                onNext={handleNext}
              />
            )}
          </div>

          {/* Right side - Testimonials */}
          <div className="md:col-span-7  overflow-hidden xl:col-span-9 w-full   md:min-h-[500px]">
            <div
              className={cn(
                "flex gap-x-6 w-full justify-end",
                safeIsMobileLandscape && "!justify-end"
              )}
            >
              {visibleTestimonials.map((testimonial, _index) => (
                <div
                  key={testimonial.id}
                  className={cn(
                    "flex-shrink-0 h-[400px] md:h-auto  hide-scrollbar overflow-y-scroll transition-all duration-500",
                    "w-full",
                    safeIsMobile ? "w-full" : safeIsTablet ? "w-[48%]" : "xl:w-[28%]"
                  )}
                >
                  <div className="bg-zinc-900 rounded-3xl p-8 h-full flex flex-col relative overflow-hidden border border-zinc-800 hover:border-yellow-500/30 transition-colors duration-300">
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-yellow-500/10 to-transparent rounded-bl-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-yellow-500/5 to-transparent rounded-tr-3xl"></div>

                    {/* Quote mark */}
                    <div className="text-6xl text-yellow-500 mb-4 font-serif leading-none opacity-80">
                      {'"'}
                    </div>

                    {/* Content */}
                    <p className="text-white mb-auto text-lg leading-relaxed font-light">
                      {testimonial.content}
                    </p>

                    {/* Author section with enhanced styling */}
                    <div className="mt-8 pt-6 border-t border-zinc-700/50">
                      <div className="font-script text-2xl text-white mb-1">
                        {testimonial.author}
                      </div>
                      <div className="text-yellow-500/80 text-sm font-medium uppercase tracking-wider">
                        {t('verifiedCustomer')}
                      </div>
                    </div>

                    {/* Star rating visual */}
                    <div className="flex mt-3 space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Mobile */}
          {canPage && (
            <CarouselNav
              className="md:hidden flex"
              onPrev={handlePrev}
              onNext={handleNext}
            />
          )}
        </div>
      </div>
    </section>
  );
}